import { z } from "zod";

/**
 * Shared routing for **App-published agent tools**, used by both the agent host
 * (which decides what tools the model sees) and the client (which decides what
 * to intercept), so the two can't drift.
 *
 * App tool names are **namespaced by the App's registry id** —
 * `@publisher/name` becomes the model-facing prefix `<publisher>__<name>__` (the
 * `@` is dropped and the `/` encoded as `__`, so the name stays `[a-z0-9_-]`).
 * This keeps an App tool from colliding with a core server tool
 * (`read`/`edit`/`bash`/…) or with another App's tool of the same bare name,
 * AND keeps a `@local/foo` App's tools distinct from a `@heath/foo` built-in's.
 * Core tool names use single underscores at most (`list_checkpoints`), never
 * `__`, so {@link parseAppToolName} returns `null` for them and the client
 * leaves them to the server.
 *
 * The id here is the **registry** identity: the DO projects the app index
 * through the deterministic winner rule (`projectAppRegistry`) before
 * assembling tools, so the model never sees two identically-named entries; the
 * client resolves the id back to the winning install's *path* (the launcher
 * identity) to pick which sandbox host executes the call.
 */
import type { AppIndexEntry } from "./index/app-index";
import { projectAppRegistry } from "./index/registry";
import { workspaceProgramSpecifier } from "./loader/paths";
import {
  type AppTool,
  AppToolSchema,
  selectToolRuntime,
  STANDARD_PAGE_RUNTIME,
  WORKER_RUNTIME,
} from "./manifest";

/** An App tool plus the registry id of the App that published it. */
export interface AppToolSpec {
  /** The App's registry id `@publisher/name` — the tool-routing key. */
  appId: string;
  /** The tool as declared in the App manifest's `tools`. */
  tool: AppTool;
  /**
   * Whether the App had an unapproved capability when this turn's tool set was
   * assembled — the App-consent gate, which becomes the tool's `needsApproval`
   * so the call suspends for a member instead of failing silently.
   *
   * A **snapshot**, deliberately, not a live read: the AI SDK re-evaluates
   * `needsApproval` when the approval answer comes back and treats a now-`false`
   * gate as "approval not required", which turns the member's Approve into a
   * silent denial. Approving the App's permissions is exactly what makes the
   * live value flip, so the gate has to be stable across that round trip.
   *
   * Safe to be stale because it is only the ASK. Enforcement never reads it:
   * capabilities are lowered through `grantedCapabilities` at launch/execute, so
   * a tool that runs with a stale gate still gets only approved capabilities.
   */
  consentPending?: boolean;
}

const appToolSpecsSchema = z.array(
  z.object({
    appId: z.string(),
    tool: AppToolSchema,
    consentPending: z.boolean().optional(),
  }),
);

/**
 * Explicit codec for carrying {@link AppToolSpec}s across the DO RPC
 * boundary. The stub's `Serializable` type mapping can't express the nested
 * schema values: `AppTool.inputSchema` is `Record<string, unknown>`, whose
 * `unknown` collapses the whole method's return type to `never`, and a
 * recursive JSON type blows up the mapping's instantiation (TS2589). So the
 * specs cross as a JSON string and are VALIDATED back into shape with the
 * manifest's own zod schema — no type assertion on either side of the wire.
 */
export function encodeAppToolSpecs(specs: AppToolSpec[]): string {
  return JSON.stringify(specs);
}

export function decodeAppToolSpecs(wire: string): AppToolSpec[] {
  return appToolSpecsSchema.parse(JSON.parse(wire));
}

/**
 * Whether an App tool is executed **server-side** — in a Worker Loader isolate,
 * assembled with a live `execute` by `buildAppTools` (`runWorkerTool` →
 * `HearthDO.runAppTool`). True whenever the tool's runtime preference list
 * includes `worker`; the DO can always satisfy `worker`, so it runs there.
 *
 * This is the routing hinge shared by the two sides so they can't drift:
 * - the DO uses it to decide which App tools get a server `execute`;
 * - the client (`use-sandbox-tools`) uses its INVERSE to decide what to run in
 *   the App's page sandbox.
 *
 * That inverse matters because the agents SDK surfaces EVERY `input-available`
 * tool call to the client's `onToolCall` — server-executed ones included. If the
 * client also ran a worker tool in the sandbox, the tool would fire twice (the
 * duplicate-Corral-job bug): once server-side and once page-side. So only a
 * page-only tool (no `worker` in its runtime) is the client's to run.
 */
export function appToolRunsInWorker(tool: AppTool): boolean {
  return selectToolRuntime(tool, [WORKER_RUNTIME]) !== undefined;
}

/**
 * Whether the CLIENT resolves this call rather than the server — the one
 * predicate `buildAppTools` (omit `execute`), the park sweep
 * (`clientToolNames`), and the browser's interceptor all read, so they cannot
 * disagree about who owes an answer.
 *
 * Two reasons a call is the client's:
 *  - the tool is **page**-only (the DO has no DOM); or
 *  - the App's consent is **pending**, whatever its runtime. A gated call must
 *    reach a member, and the browser is the only place that can ask — so the
 *    tool is built with no `execute`, parks at `input-available` like any other
 *    client tool, and the chat resolves it once the member decides.
 *
 * That second arm is what makes the two runtimes behave identically while an
 * App is unapproved: one suspension, one card, one answer door. (`needsApproval`
 * can't do this job — the AI SDK forwards the tool call to the UI *before* it
 * checks the gate, so a client-run tool executes regardless, and it re-resolves
 * the gate on the continuation turn, turning an approval into a denial once
 * consent lands. See `validate-tool-approvals.ts` in the `ai` package.)
 */
export function appToolResolvedByClient(spec: AppToolSpec): boolean {
  return spec.consentPending === true || !appToolRunsInWorker(spec.tool);
}

/**
 * The model-facing names of every App tool the BROWSER resolves — exactly the
 * specs {@link appToolResolvedByClient} selects, so it names precisely the tools
 * `useSandboxTools` answers with `addToolOutput` (by running them, or by asking
 * a member first).
 *
 * Used to tell the agents SDK which `input-available` parts are legitimately
 * *waiting on the client* rather than dead server orphans (see
 * `ChatHostDO`'s client-tool park sweep). Derived from the same specs
 * `buildAppTools` projects, so the two can't name different tools — and that
 * matters doubly now: a consent-gated worker tool is built with no `execute`, so
 * if it were missing here its parked call would read as a dead server orphan.
 */
export function clientAppToolNames(specs: readonly AppToolSpec[]): string[] {
  const names: string[] = [];
  for (const spec of specs) {
    if (!appToolResolvedByClient(spec)) continue;
    names.push(appToolName(spec.appId, spec.tool.name));
  }
  return names;
}

/**
 * How the client should handle a tool call the agents SDK routed to
 * `onToolCall`:
 * - `server` — a core server tool, or an App tool that runs in the worker
 *   (already executing server-side). The client does nothing; running it here
 *   too would double-execute it.
 * - `missing` — an App tool whose App isn't installed (can't be resolved to a
 *   sandbox). The client answers with an error so the turn isn't bricked by a
 *   never-resolved tool call.
 * - `page` — a page-only App tool the client runs in the resolved App's sandbox.
 *
 * Pure so the routing decision is unit-testable without a live client session —
 * the double-execution guard lives here, not in React effects.
 */
export type SandboxToolRoute =
  | { kind: "server" }
  | { kind: "missing"; appId: string }
  /**
   * The App's permissions aren't granted, so the call is neither the server's
   * to run nor the client's to dispatch — it's a question for a member. The
   * interceptor leaves it parked at `input-available` and the chat renders a
   * consent card; answering it resolves the park.
   */
  | { kind: "consent"; appId: string; path: string; toolName: string }
  | {
      kind: "page";
      appId: string;
      /** The winning install's root path — the sandbox host key. */
      path: string;
      /** The bare (un-namespaced) tool name the App page export is keyed by. */
      toolName: string;
      /** The manifest spec, when the tool is declared (absent ⇒ authoring gap). */
      tool?: AppTool;
    };

/**
 * The App a tool call belongs to — its registry id and the winning install's
 * path — or null for a core server tool or an uninstalled App. Lets a caller
 * look up per-App state (consent) BEFORE routing, which
 * {@link routeAppToolCall} then takes as an input rather than fetching itself.
 */
export function appToolTarget(
  toolName: string,
  entries: Iterable<AppIndexEntry>,
): { appId: string; path: string } | null {
  const parsed = parseAppToolName(toolName);
  if (!parsed) return null;
  const target = projectAppRegistry(entries).byId.get(parsed.appId);
  return target ? { appId: parsed.appId, path: target.path } : null;
}

export function routeAppToolCall(
  toolName: string,
  entries: Iterable<AppIndexEntry>,
  /**
   * Whether THIS call's App still has an unapproved capability. The caller owns
   * grant state (and has already resolved the App via {@link appToolTarget} to
   * look it up); omitted ⇒ treat it as approved. Checked BEFORE the runtime
   * split, because an unapproved App's call is a question for a member no matter
   * where it would eventually run.
   */
  consentPending?: boolean,
  /** The call's input: a tool that runs in both places can ask for the page. */
  input?: unknown,
): SandboxToolRoute {
  // Core server tools (`read`/`edit`/`bash`/…) have no `__` — leave them be.
  const parsed = parseAppToolName(toolName);
  if (!parsed) return { kind: "server" };

  // Resolve the id to the winning install the DO also chose (same projection),
  // then find the tool's manifest spec so we can read its runtime.
  const target = projectAppRegistry(entries).byId.get(parsed.appId);
  const spec = target?.manifest?.tools?.find((t) => t.name === parsed.toolName);

  if (!target) return { kind: "missing", appId: parsed.appId };

  // Unapproved: the server built this tool with no `execute` whatever its
  // runtime, so the call is parked waiting on a member — mirroring
  // `appToolResolvedByClient`, which the DO used to decide that.
  if (consentPending) {
    return {
      kind: "consent",
      appId: parsed.appId,
      path: target.path,
      toolName: parsed.toolName,
    };
  }

  // A worker tool is the server's to run — never the client's (see
  // `appToolRunsInWorker`). This is the guard against double execution.
  if (spec && appToolRunsInWorker(spec) && !appToolCallAsksForPage(spec, input))
    return { kind: "server" };
  return {
    kind: "page",
    appId: parsed.appId,
    path: target.path,
    toolName: parsed.toolName,
    tool: spec,
  };
}

const SEP = "__";

/**
 * The model-facing tool name for an App tool:
 * `@publisher/name` + `list_events` → `publisher__name__list_events`.
 */
export function appToolName(appId: string, toolName: string): string {
  return `${appId.replace(/^@/, "").replace("/", SEP)}${SEP}${toolName}`;
}

/** The module a page tool's call runs in: its own entrypoint, else the page's. */
export function pageToolModule(
  appPath: string,
  tool: AppTool | undefined,
): string | undefined {
  return tool?.entrypoint
    ? workspaceProgramSpecifier(appPath, tool.entrypoint)
    : undefined;
}

/** An App's namespace in eval's `tools`: `@heath/pixelpusher` → `heath.pixelpusher`. */
export function appNamespace(appId: string): string {
  return appId.replace(/^@/, "").replace("/", ".");
}

/**
 * Whether THIS call of a tool that runs in both places asks for the page
 * (`runtime: "page"` in its input, as the Console's `eval` takes). The server
 * parks such a call instead of running it, and the browser runs it: one rule,
 * so the two can't both, or neither, answer it.
 */
export function appToolCallAsksForPage(tool: AppTool, input: unknown): boolean {
  return (
    appToolRunsInWorker(tool) &&
    selectToolRuntime(tool, [STANDARD_PAGE_RUNTIME]) !== undefined &&
    z.object({ runtime: z.literal("page") }).safeParse(input).success
  );
}

/**
 * Inverse of {@link appToolName}. The first two `__`-delimited segments are the
 * publisher + App name (both slug-safe, no `__`); everything after the second
 * `__` is the tool name (which may itself contain `__`). Returns `null` for a
 * name with fewer than two `__` — i.e. a core server tool — which the caller
 * leaves to the server.
 */
export function parseAppToolName(
  name: string,
): { appId: string; toolName: string } | null {
  const i1 = name.indexOf(SEP);
  if (i1 < 0) return null;
  const i2 = name.indexOf(SEP, i1 + SEP.length);
  if (i2 < 0) return null;
  const publisher = name.slice(0, i1);
  const appName = name.slice(i1 + SEP.length, i2);
  return {
    appId: `@${publisher}/${appName}`,
    toolName: name.slice(i2 + SEP.length),
  };
}

/**
 * Every App-published tool spec, tagged with its App's registry id so
 * `buildAppTools` can namespace it. A broken manifest simply has no `manifest`
 * on its index entry — the App contributes nothing and the rest still load
 * (the authoring error surfaces at write time via
 * `validateAppManifestForPath`, and in the listing UI via the entry's
 * `error`).
 */
export function getAppToolSpecs(
  entries: readonly AppIndexEntry[],
  /**
   * The App-consent gate, snapshotted onto each spec (see
   * {@link AppToolSpec.consentPending}). Omitted ⇒ no tool is gated, for callers
   * with no grant store to consult (the schedule projection, tests).
   */
  consentPending?: (appId: string, entry: AppIndexEntry) => boolean,
): AppToolSpec[] {
  const { byId } = projectAppRegistry(entries);
  const specs: AppToolSpec[] = [];
  for (const [appId, entry] of byId) {
    const tools = entry.manifest?.tools ?? [];
    if (tools.length === 0) continue;
    const gated = consentPending?.(appId, entry) ?? false;
    for (const tool of tools) {
      specs.push({ appId, tool, ...(gated ? { consentPending: true } : {}) });
    }
  }
  return specs;
}
